import PropTypes from 'prop-types';

import useTwitchConfig from '@hooks/useTwitchConfig.js';
import { useTwitchCampaigns } from '@hooks/useTwitchCampaigns.js';
import { useEffect, useMemo } from 'preact/hooks';
import { Check } from 'flowbite-react-icons/outline';
import { formatCurrencyNumber } from '@util/common.js';

TwitchCampaignViewer.propTypes = {
  onCampaignSelected: PropTypes.func,
  disabled: PropTypes.bool,
};

export default function TwitchCampaignViewer({ onCampaignSelected, disabled }) {
  const config = useTwitchConfig();
  const { campaigns, campaignsLoading, writeCampaign } = useTwitchCampaigns(
    config,
    disabled
  );

  const selectedCampaign = useMemo(() => {
    if (!campaigns) return null;
    return campaigns[0];
  }, [campaigns]);

  const hasNoCampaigns = useMemo(() => {
    return (
      !campaignsLoading && campaigns !== undefined && campaigns.length === 0
    );
  }, [campaigns, campaignsLoading]);

  useEffect(() => {
    if (selectedCampaign && !disabled) {
      const { id, target_amount, charity_name } = selectedCampaign;
      config.twitchCampaignID = id;
      config.twitchCampaignGoal = `${target_amount.value}`;
      config.twitchCampaignName = charity_name;

      const campaignGoal =
        target_amount.value / Math.pow(10, target_amount['decimal_places']);

      localStorage.setItem('twitchCampaignID', id);
      localStorage.setItem('twitchCampaignGoal', `${campaignGoal}`);
      localStorage.setItem('twitchCampaignName', charity_name);

      writeCampaign(selectedCampaign).then((data) => {
        if (data) {
          onCampaignSelected({
            id: data.id,
            goal: `${data['targetAmount'].value}`,
            name: data['charity'].name,
          });
        }
      });
    }
  }, [selectedCampaign, disabled]);

  useEffect(() => {
    if (!hasNoCampaigns) {
      onCampaignSelected(null);
      config.twitchCampaignID = '';
      config.twitchCampaignGoal = '0.00';
      config.twitchCampaignName = '';
      localStorage.removeItem('twitchCampaignID');
      localStorage.removeItem('twitchCampaignGoal');
      localStorage.removeItem('twitchCampaignName');
    }
  }, [hasNoCampaigns]);

  const campaignGoal = useMemo(() => {
    if (!selectedCampaign) return 0.0;
    const { target_amount } = selectedCampaign;
    return target_amount.value / Math.pow(10, target_amount['decimal_places']);
  }, [selectedCampaign]);

  if (campaignsLoading)
    return (
      <div className="h-24 w-full flex justify-center items-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );

  if (hasNoCampaigns)
    return (
      <div className="h-24 w-full flex flex-col justify-center">
        <article className="text-gray-500 prose text-sm ml-2">
          You do not have a St. Jude charity campaign active
        </article>
        <br />
        <p className="text-gray-500 prose text-sm ml-2">
          Please visit{' '}
          <a
            className="link"
            href="https://help.twitch.tv/s/article/twitch-charity?language=en_US"
            target="_blank"
            rel="noopener noreferrer"
          >
            this page
          </a>{' '}
          for more information.
        </p>
      </div>
    );

  return (
    <div className="h-24">
      <div className="w-full h-18 border-2 rounded-lg bg-base-100 select-none">
        <div className="flex items-center px-3 py-4">
          {selectedCampaign && (
            <img
              className="w-10"
              src={selectedCampaign['charity_logo']}
              alt="Charity Logo"
            />
          )}
          {selectedCampaign && (
            <div className="ml-4">
              <h1 className="text-2xl">{selectedCampaign['charity_name']}</h1>
              <h3>Goal: ${formatCurrencyNumber(campaignGoal)}</h3>
            </div>
          )}
          {selectedCampaign && (
            <div className="flex-grow flex justify-items-end text-2xl">
              <span className="flex-grow" />
              <Check className="text-success w-12" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
